<script lang="ts" setup>
const props = defineProps({
  document: {
    type: Object as PropType<SubmittedDocument>,
    required: true,
  },
});

const submittedDocument = ref<SubmittedDocument>({ ...props.document });

const emit = defineEmits(["close"]);

const userClassroomStore = useUserClassroomsDataStore();
const { classrooms, allAssignments, allStudents } =
  storeToRefs(userClassroomStore);

const appDataStore = useAppUserDataStore();
const { isPublicDemo } = storeToRefs(appDataStore);

const onChangeClassroom = () => {
  const matchingClassroom = classrooms.value.find(
    (classroom) => classroom.id === submittedDocument.value.classroomId
  );

  if (matchingClassroom == undefined) return;

  submittedDocument.value.classroomName = matchingClassroom.label;
};

const onChangeStudent = () => {
  const matchingStudent = allStudents.value.find(
    (student) => student.id === submittedDocument.value.studentId
  );

  if (matchingStudent == undefined) return;

  submittedDocument.value.studentName = matchingStudent.name;
};

const onChangeAssignment = () => {
  const matchingAssignment = allAssignments.value.find(
    (assignment) => assignment.id === submittedDocument.value.assignmentId
  );

  if (matchingAssignment == undefined) return;

  submittedDocument.value.assignmentName = matchingAssignment.name;
  submittedDocument.value.dueTime = matchingAssignment.dueTimestamp;
  submittedDocument.value.checklistId =
    matchingAssignment.checklistId ?? undefined;
  submittedDocument.value.checklistPath =
    matchingAssignment.checklistPath ?? undefined;
};

const sortedClassrooms = computed(() => {
  return classrooms.value.sort((a, b) => a.label.localeCompare(b.label));
});

const classroomAssignments = computed(() => {
  if (submittedDocument.value.classroomId == undefined) return [];

  return useSortAssignments(
    allAssignments.value.filter(
      (assignment) =>
        assignment.classroomId === submittedDocument.value.classroomId
    )
  );
});

const classroomStudents = computed(() => {
  if (submittedDocument.value.classroomId == undefined) return [];

  return allStudents.value
    .filter((student) =>
      student.classroomIds.includes(submittedDocument.value.classroomId!)
    )
    .sort((a, b) => a.name.localeCompare(b.name));
});

const isSavingDocument = ref(false);

const saveDocument = async () => {
  if (isPublicDemo.value) {
    useBaseAlert("Oops!", "You can’t edit submissions in demo mode.");
    return;
  }

  if (
    submittedDocument.value.classroomId != undefined &&
    (submittedDocument.value.studentId == undefined ||
      submittedDocument.value.assignmentId == undefined)
  ) {
    useBaseToast("Please select a student and an assignment", "error");
    return;
  }

  isSavingDocument.value = true;

  const hasSameChecklist =
    props.document.checklistId === submittedDocument.value.checklistId;

  await useDocuments().updateDocument(submittedDocument.value);

  if (hasSameChecklist) {
    onSaveSuccess();
    return;
  }

  if (
    submittedDocument.value.checklistId == undefined ||
    submittedDocument.value.id == undefined
  ) {
    onSaveSuccess();
    return;
  }

  // Trigger the change checklist process.
  await useApplyChecklistToDocuments().applyChecklistToDocumentNow(
    submittedDocument.value.checklistId!,
    submittedDocument.value.checklistPath ?? "/checklists",
    submittedDocument.value.id!
  );

  onSaveSuccess();
};

const onSaveSuccess = () => {
  useBaseToast("Document saved successfully", "success");
  emit("close");
};

const assignmentSubmissionTypes = computed(() => {
  const assignmentId = submittedDocument.value.assignmentId;

  if (assignmentId == undefined) return [];

  const assignment = allAssignments.value.find(
    (assignment) => assignment.id === assignmentId
  );

  if (assignment == undefined) return [];

  const allTypes = DocumentSubmissionTypes;

  const classroom = classrooms.value.find(
    (classroom) => classroom.id === submittedDocument.value.classroomId
  );

  if (classroom == undefined) return [];

  return allTypes.filter((type) => {
    return assignment.allowsSubmissionType({
      classroom,
      submissionType: type.value,
    });
  });
});
</script>

<template>
  <div class="p-4 pb-8 flex flex-col h-full overflow-auto">
    <BaseFormComponent>
      <template #label> Classroom </template>
      <BaseSelectInput
        v-model="submittedDocument.classroomId"
        :options="sortedClassrooms"
        value-prop="id"
        label="label"
        :searchable="true"
        @update:model-value="onChangeClassroom"
      />
    </BaseFormComponent>
    <TransitionsCollapsible>
      <div
        v-if="submittedDocument.classroomId"
        :key="submittedDocument.classroomId"
        class="flex flex-col"
      >
        <BaseFormComponent>
          <template #label> Student </template>
          <BaseSelectInput
            v-model="submittedDocument.studentId"
            :options="classroomStudents"
            value-prop="id"
            label="name"
            :searchable="true"
            @update:model-value="onChangeStudent"
          />
        </BaseFormComponent>
        <BaseFormComponent>
          <template #label> Assignment </template>
          <BaseSelectInput
            v-model="submittedDocument.assignmentId"
            :options="classroomAssignments"
            value-prop="id"
            label="name"
            :searchable="true"
            @update:model-value="onChangeAssignment"
          />
        </BaseFormComponent>
        <BaseFormComponent
          v-if="
            submittedDocument.assignmentId &&
            assignmentSubmissionTypes.length > 1
          "
        >
          <template #label> Submission Type </template>
          <BaseSelectInput
            :options="assignmentSubmissionTypes"
            v-model="submittedDocument.documentSubmissionType"
          />
        </BaseFormComponent>
      </div>
    </TransitionsCollapsible>
    <BaseFormComponent
      v-if="
        submittedDocument.documentSubmissionType == undefined ||
        submittedDocument.documentSubmissionType ==
          DocumentSubmissionType.finalDraft
      "
    >
      <template #label>
        <div class="flex flex-row items-center">
          Use Accepted/Incomplete for final draft
          <BaseHint
            class="ml-2 mt-[1px]"
            hint="Papers are graded with a numerical score by default. "
          />
        </div>
      </template>
      <BaseToggle v-model="submittedDocument.allowFinalDraftGradedState" />
    </BaseFormComponent>
    <BaseButton :show-spinner="isSavingDocument" @click="saveDocument">
      Save
    </BaseButton>
  </div>
</template>

<style scoped></style>
